import type { AgencyPortalUser } from "../types";
import {
  isShadowPortalUser,
  isStaffEmail,
  isStaffPortalUser,
} from "./staffShadowAccounts";

/**
 * Self-serve signups that confirmed their e-mail but never created an
 * agency (`agency_portal_users.status = 'pending_onboarding'`, agency_id
 * NULL). The agency list cannot show them (it groups logins by agency), so
 * they were invisible in the CRM (plan 2026-10-08 §1.4, REF-143).
 */
export const isPendingSignup = (
  user: Pick<AgencyPortalUser, "status">,
): boolean => user.status === "pending_onboarding";

/** Staff logins and shadow accounts: hidden while "Hide internal" is on. */
export const isInternalSignup = (
  user: Pick<AgencyPortalUser, "email" | "is_staff" | "shadow_of_sales_id">,
  roster: readonly { email?: string | null; disabled?: boolean | null }[],
): boolean =>
  isStaffPortalUser(user) ||
  isShadowPortalUser(user) ||
  isStaffEmail(user.email, roster);

/** Pending signups, newest first; staff hidden unless `hideInternal` is off. */
export function pendingSignups<T extends AgencyPortalUser>(
  users: readonly T[],
  {
    hideInternal,
    roster,
  }: {
    hideInternal: boolean;
    roster: readonly { email?: string | null; disabled?: boolean | null }[];
  },
): { visible: T[]; hiddenInternal: number } {
  const pending = users
    .filter(isPendingSignup)
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
  if (!hideInternal) return { visible: pending, hiddenInternal: 0 };
  const visible = pending.filter((user) => !isInternalSignup(user, roster));
  return { visible, hiddenInternal: pending.length - visible.length };
}

/** The lead in the RE funnel tab: its lead list searched by this e-mail. */
export const reFunnelSearchHref = (email: string): string =>
  `/re-funnel?${new URLSearchParams({ q: email.trim().toLowerCase() })}`;

/** "just now", "12 min ago", "3 h ago", "2 d ago", then the date. */
export function relativeTime(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!iso) return "never";
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return "—";
  const minutes = Math.max(0, Math.round((now - at) / 60_000));
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 45) return `${days} d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
